import { api } from "@infrastructure/api";
import { ConversationSSEEventSchema, } from "../models";
// =============================================================================
// CONSTANTS
// =============================================================================
const SSE_ENDPOINT = "/conversations/events";
const BACKOFF_DELAYS = [1000, 2000, 5000, 10000, 30000];
// =============================================================================
// SSE SERVICE
// =============================================================================
/**
 * Manages a persistent SSE connection to GET /conversations/events.
 * Uses Axios with adapter:'fetch' + responseType:'stream' so the
 * Authorization and X-Org-Id headers are auto-injected by the interceptor.
 *
 * On stream close or error, reconnects with exponential backoff.
 */
export const conversationSseService = {
    _abortController: null,
    _reconnectTimer: null,
    _backoffIndex: 0,
    _isConnected: false,
    _callbacks: null,
    connect(callbacks) {
        this._callbacks = callbacks;
        this._backoffIndex = 0;
        this._startConnection();
    },
    disconnect() {
        this._clearReconnectTimer();
        this._abortController?.abort();
        this._abortController = null;
        this._isConnected = false;
        this._callbacks = null;
    },
    _startConnection() {
        this._abortController?.abort();
        this._abortController = new AbortController();
        const signal = this._abortController.signal;
        api
            .get(SSE_ENDPOINT, {
            responseType: "stream",
            adapter: "fetch",
            signal,
        })
            .then(async (response) => {
            this._isConnected = true;
            this._backoffIndex = 0;
            this._callbacks?.onConnect?.();
            const reader = response.data.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            try {
                let reading = true;
                while (reading) {
                    const { done, value } = await reader.read();
                    if (done) {
                        reading = false;
                        break;
                    }
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() ?? "";
                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed.startsWith("data:"))
                            continue;
                        const json = trimmed.slice(5).trim();
                        if (!json)
                            continue;
                        this._dispatchEvent(json);
                    }
                }
            }
            catch (err) {
                if (err?.name === "AbortError")
                    return;
                this._callbacks?.onError?.(err);
            }
            finally {
                this._isConnected = false;
                this._callbacks?.onDisconnect?.();
                if (!signal.aborted) {
                    this._scheduleReconnect();
                }
            }
        })
            .catch((err) => {
            if (err?.name === "AbortError" || err?.code === "ERR_CANCELED")
                return;
            const status = err?.response?.status;
            if (status === 401 || status === 403) {
                this._isConnected = false;
                this._callbacks = null;
                return;
            }
            this._callbacks?.onError?.(err);
            this._isConnected = false;
            this._scheduleReconnect();
        });
    },
    _dispatchEvent(json) {
        try {
            const parsed = JSON.parse(json);
            const event = ConversationSSEEventSchema.parse(parsed);
            this._callbacks?.onEvent(event);
        }
        catch {
            // silently skip malformed events
        }
    },
    _scheduleReconnect() {
        if (!this._callbacks)
            return;
        this._clearReconnectTimer();
        const delay = BACKOFF_DELAYS[Math.min(this._backoffIndex, BACKOFF_DELAYS.length - 1)];
        this._backoffIndex = Math.min(this._backoffIndex + 1, BACKOFF_DELAYS.length - 1);
        this._reconnectTimer = setTimeout(() => {
            if (this._callbacks) {
                this._startConnection();
            }
        }, delay);
    },
    _clearReconnectTimer() {
        if (this._reconnectTimer !== null) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
    },
};
export default conversationSseService;
